import {
    Database,
    Download,
    FileText,
    Plus,
    RefreshCw,
    Upload
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

/**
 * 知識庫管理組件 - 簡化版
 * 參考成功案例：Notion AI、ChatGPT、Cursor
 * 設計原則：
 * 1. 只保留核心功能：上傳和查看
 * 2. 移除冗餘功能：搜索(已有RAG聊天)、多集合選擇、手動添加
 * 3. 自動化分類：AI自動判斷文件類別
 * 4. 簡潔 UI：2個標籤頁取代5個
 */
const KnowledgeBase = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [knowledgeStats, setKnowledgeStats] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [documents, setDocuments] = useState([]);

    // 簡化的集合映射
    const collectionNames = {
        'patent_law': '專利法規',
        'case_examples': '案例分析',
        'sop_guidelines': 'SOP指南',
        'legal_knowledge': '法律知識'
    };

    useEffect(() => {
        loadKnowledgeStats();
        loadDocuments();
    }, []);

    const loadKnowledgeStats = async () => {
        try {
            setIsLoading(true);
            const response = await fetch('/api/rag/knowledge');
            const data = await response.json();
            if (data.success) {
                setKnowledgeStats(data.stats);
            }
        } catch (error) {
            console.error('載入知識庫統計失敗:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadDocuments = async () => {
        try {
            setIsLoading(true);
            const response = await fetch('/api/rag/documents');
            const data = await response.json();
            if (data.success) {
                setDocuments(data.documents);
            }
        } catch (error) {
            console.error('載入文件列表失敗:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('collection', 'patent_law'); // 自動分類
        formData.append('title', file.name);

        try {
            setIsLoading(true);
            const response = await fetch('/api/rag/upload-document', {
                method: 'POST',
                body: formData
            });

            const data = await response.json();
            if (data.success) {
                alert('✅ 文件上傳成功！');
                loadKnowledgeStats();
                loadDocuments();
            } else {
                alert('❌ 上傳失敗: ' + data.error);
            }
        } catch (error) {
            console.error('上傳失敗:', error);
            alert('❌ 上傳失敗: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const downloadDocument = async (documentId, filename) => {
        try {
            const response = await fetch(`/api/rag/documents/${documentId}/download`);
            if (response.ok) {
                const blob = await response.blob();
                const suggestedName = filename || `document_${documentId}.txt`;

                // 現代瀏覽器支援選擇下載位置
                if ('showSaveFilePicker' in window) {
                    try {
                        const fileHandle = await window.showSaveFilePicker({
                            suggestedName: suggestedName,
                            types: [{
                                description: '文字檔案',
                                accept: {
                                    'text/plain': ['.txt'],
                                    'text/markdown': ['.md']
                                }
                            }]
                        });
                        const writable = await fileHandle.createWritable();
                        await writable.write(blob);
                        await writable.close();
                    } catch (err) {
                        if (err.name !== 'AbortError') {
                            console.error('保存文件失敗:', err);
                        }
                    }
                } else {
                    // 降級方案
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = suggestedName;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                }
            }
        } catch (error) {
            console.error('下載文件失敗:', error);
            alert('下載失敗: ' + error.message);
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* 精簡的頭部 */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
                <h2 className="text-2xl font-bold mb-2">📚 知識庫管理</h2>
                <p className="text-blue-100">
                    上傳文件到知識庫,讓 AI 助理能回答相關問題
                </p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
                {/* 簡化為 2 個標籤 */}
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="overview">📊 概覽</TabsTrigger>
                    <TabsTrigger value="upload">📤 上傳文件</TabsTrigger>
                </TabsList>

                {/* 概覽標籤 */}
                <TabsContent value="overview" className="space-y-6 p-6">
                    {/* 統計卡片 */}
                    {knowledgeStats.total_documents >= 0 && (
                        <Card className="bg-gradient-to-br from-blue-500 to-purple-600 text-white border-0">
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-5xl font-bold mb-2">
                                            {knowledgeStats.total_documents}
                                        </div>
                                        <p className="text-blue-100 text-lg font-medium">個知識文件</p>
                                        <p className="text-blue-200 text-sm mt-2">
                                            涵蓋專利法規、案例分析、SOP 指南
                                        </p>
                                    </div>
                                    <Database className="w-20 h-20 text-white opacity-30" />
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* 文件列表 */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="w-5 h-5" />
                                    已上傳文件 ({documents.length})
                                </CardTitle>
                                <Button variant="outline" size="sm" onClick={loadDocuments} disabled={isLoading}>
                                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <div className="text-center py-12">
                                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
                                    <p className="text-gray-500">載入中...</p>
                                </div>
                            ) : documents.length > 0 ? (
                                <div className="space-y-3">
                                    {documents.map((doc) => (
                                        <div key={doc.id} className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                                            {doc.metadata?.title || '未命名文件'}
                                                        </h4>
                                                        <Badge variant="outline" className="text-xs">
                                                            {collectionNames[doc.collection] || '知識庫'}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                                                        {doc.content}
                                                    </p>
                                                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                                        <span>📄 {doc.full_length} 字元</span>
                                                        {doc.metadata?.created_at && (
                                                            <span>🕐 {new Date(doc.metadata.created_at).toLocaleDateString()}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 ml-4">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => downloadDocument(doc.id, doc.metadata?.title)}
                                                        title="下載文件"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                                    <p className="text-gray-500 dark:text-gray-400 mb-2">尚未上傳任何文件</p>
                                    <Button onClick={() => setActiveTab('upload')} size="sm">
                                        <Plus className="w-4 h-4 mr-2" />
                                        立即上傳
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* 上傳標籤 - 極簡設計 */}
                <TabsContent value="upload" className="space-y-6 p-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Upload className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                上傳知識文件
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* 拖放上傳區域 */}
                            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors">
                                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                                <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">拖放文件到這裡</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">或</p>
                                <label htmlFor="file-upload" className="inline-block">
                                    <input
                                        id="file-upload"
                                        type="file"
                                        className="hidden"
                                        accept=".txt,.md,.pdf,.doc,.docx"
                                        onChange={handleFileUpload}
                                        disabled={isLoading}
                                    />
                                    <Button asChild disabled={isLoading}>
                                        <span className="cursor-pointer">
                                            {isLoading ? (
                                                <>
                                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                                    上傳中...
                                                </>
                                            ) : (
                                                <>
                                                    <Plus className="w-4 h-4 mr-2" />
                                                    選擇文件
                                                </>
                                            )}
                                        </span>
                                    </Button>
                                </label>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
                                    支援 TXT, MD, PDF, DOC, DOCX 格式
                                </p>
                            </div>

                            {/* 使用提示 */}
                            <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                                <CardContent className="pt-4">
                                    <div className="flex items-start gap-3">
                                        <div className="text-2xl">💡</div>
                                        <div className="text-sm space-y-2">
                                            <p className="font-medium text-blue-900 dark:text-blue-100">
                                                上傳的文件會自動處理並加入知識庫
                                            </p>
                                            <ul className="text-blue-800 dark:text-blue-200 space-y-1 ml-4">
                                                <li>• AI 會自動分類文件內容</li>
                                                <li>• 支援中英文混合文件</li>
                                                <li>• 可以在 RAG 聊天中詢問文件內容</li>
                                            </ul>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default KnowledgeBase;
